import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaHouse, FaUser, FaBriefcase, FaCode, FaEnvelope } from 'react-icons/fa6';
import Hero from './components/Hero';
import About from './components/About';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Contact from './components/Contact';

type Section = 'home' | 'about' | 'projects' | 'skills' | 'contact';

function App() {
  const [activeSection, setActiveSection] = useState<Section>('home');

  // Écouter l'événement de navigation personnalisé
  useEffect(() => {
    const handleNavigation = (event: CustomEvent<Section>) => {
      setActiveSection(event.detail);
    };

    // Typage personnalisé pour TypeScript
    window.addEventListener('navigateToSection', handleNavigation as EventListener);

    // Nettoyer l'écouteur d'événement lors du démontage du composant
    return () => {
      window.removeEventListener('navigateToSection', handleNavigation as EventListener);
    };
  }, []);

  const sections: { id: Section; icon: React.ReactNode; label: string }[] = [
    { id: 'home', icon: <FaHouse className="w-4 h-4" />, label: 'Accueil' },
    { id: 'about', icon: <FaUser className="w-4 h-4" />, label: 'À propos' },
    { id: 'projects', icon: <FaBriefcase className="w-4 h-4" />, label: 'Projets' },
    { id: 'skills', icon: <FaCode className="w-4 h-4" />, label: 'Compétences' },
    { id: 'contact', icon: <FaEnvelope className="w-4 h-4" />, label: 'Contact' },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case 'home':
        return <Hero />;
      case 'about':
        return <About />;
      case 'projects':
        return <Projects />;
      case 'skills':
        return <Skills />;
      case 'contact':
        return <Contact />;
      default:
        return <Hero />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col">
      {/* Top Navigation */}
      <nav className="fixed top-2 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-[95vw] sm:max-w-max px-2 sm:px-0">
        <div className="backdrop-blur-md bg-gray-900/30 border border-gray-700/50 rounded-full shadow-xl w-full overflow-x-auto sm:overflow-visible scrollbar-hide">
          <div className="flex items-center space-x-1 sm:space-x-2 py-1 px-1 sm:px-4">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex flex-col items-center px-2 sm:px-3 py-1 rounded-full transition-all duration-200 text-xs min-w-[60px] sm:min-w-max ${
                  activeSection === section.id
                    ? 'bg-gradient-to-br from-green-500/90 to-green-600/90 text-white shadow-md shadow-green-500/20 transform scale-105'
                    : 'text-gray-300 hover:bg-gray-700/30 hover:text-white hover:scale-105'
                }`}
                title={section.label}
              >
                <span className="text-sm">{section.icon}</span>
                <span className="text-[9px] mt-0.5 whitespace-nowrap">{section.label}</span>
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow pt-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeSection}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            style={{ height: '100%' }}
          >
            {renderSection()}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Footer - Ne s'affiche pas sur la page d'accueil */}
      {activeSection !== 'home' && (
        <footer className="py-6 bg-gray-900/80 backdrop-blur-sm border-t border-gray-800">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Rani Hamed. Tous droits réservés.
            </p>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
